// Terminal dues / final-settlement client — talks to payroll-service through the
// gateway (gatewayApi attaches the JWT). Computes leavers' final pay: prorated
// final salary, accrued-leave payout, severance, and payment in lieu of notice.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1';

export type ExitType = 'termination' | 'redundancy' | 'resignation' | 'retirement' | 'death';

export interface SettlementLine {
  code: string;
  label: string;
  amount: string;
  detail: string;
}

export interface Settlement {
  id: string;
  employee_id?: string;
  exit_type: ExitType;
  exit_date: string;
  hire_date?: string | null;
  years_of_service: number;
  currency: string;
  gross: string;
  lines: SettlementLine[];
  created_at?: string;
  /** Stamped when the terminal-dues payout batch settles all-paid. */
  disbursed_at?: string | null;
}

export interface TerminalConfig {
  severance_days_per_year: string;
  severance_daily_divisor: string;
  max_severance_years: number;
  leave_payout_divisor: string;
  notice_months: string;
  is_default: boolean;
}

export interface PayrollEmployeeLite {
  employee_id: string;
  full_name: string;
  legal_entity_id: string | null;
}

export const listPayrollEmployees = () =>
  gatewayApi.get<{ employees: PayrollEmployeeLite[] }>(`${BASE}/payroll/employees`).then((r) => r.data.employees ?? []);

export const computeTerminalDues = (
  employeeId: string,
  body: { exit_type: ExitType; exit_date: string; leave_balance_days?: string },
) => gatewayApi.post<Settlement>(`${BASE}/payroll/employees/${employeeId}/terminal-dues`, body).then((r) => r.data);

export const listTerminalSettlements = (employeeId: string) =>
  gatewayApi
    .get<{ settlements: Settlement[] }>(`${BASE}/payroll/employees/${employeeId}/terminal-dues`)
    .then((r) => r.data.settlements ?? []);

export const getTerminalConfig = (legalEntityId: string) =>
  gatewayApi.get<TerminalConfig>(`${BASE}/legal-entities/${legalEntityId}/terminal-dues-config`).then((r) => r.data);

export const setTerminalConfig = (legalEntityId: string, body: Partial<Omit<TerminalConfig, 'is_default'>>) =>
  gatewayApi.put<TerminalConfig>(`${BASE}/legal-entities/${legalEntityId}/terminal-dues-config`, body).then((r) => r.data);
